import React, { useContext, useMemo } from 'react';

import ComponentTypesIconWrapper from '../../components/componentTypesIcon/ComponentTypesIconWrapper';
// components
import AampeToggleButton, {
    ToggleButtonProps,
} from '../../components/shared/AampeToggleButton/AampeToggleButton';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// context
import { EditorContext } from '../../context/EditorContext';

import useComponentTypesQuery from '../../hooks/component-types/useComponentTypesQuery';

import { ComponentTypeMapped } from '../../models/component-types-model';
// models
import { ElementKind } from '../../models/editor.model';
// constant
import {
    FormulaComponentModelV1,
    FormulaTypesData,
} from '../../models/formula.model';

import ContentTag from '../formulaCreate/formulaEditor/ContentTag';
import MessagePreviewMain from '../formulaCreate/messagePreview/MessagePreviewMain';
import FormulaSidebar from './FormulaSidebar';

export enum ToggleButtonValues {
    VIEW_VARIATION = 'VIEW_VARIATION',
    PREVIEW = 'PREVIEW',
    TEST_MESSAGE = 'TEST_MESSAGE',
    CONTENT_TAG = 'CONTENT_TAG',
}

interface SidebarViewMainProps {
    isVariationTab: ToggleButtonValues;
    setIsVariationTab: (value: ToggleButtonValues) => void;
    selectedElement: Record<string, any> | null;
    components: FormulaComponentModelV1[];
    hideToggleButtons?: boolean;
}

const SidebarViewMain: React.FC<React.PropsWithChildren<SidebarViewMainProps>> =
    ({
        isVariationTab,
        setIsVariationTab,
        selectedElement,
        components,
        hideToggleButtons,
    }) => {
        const {
            editorDetails: { formulaType, formula },
        } = useContext(EditorContext);

        const { data: componentTypes } = useComponentTypesQuery();

        const isDataset = selectedElement?.kind === ElementKind.dataset;
        const isCMSPlaceholder =
            selectedElement?.kind === ElementKind.cms_placeholder;

        const variantType = selectedElement?.variantType;

        const componentsTypeData = useMemo(() => {
            return componentTypes?.map[variantType];
        }, [componentTypes, variantType]);

        const ComponentIcon = useMemo(
            () => (
                <ComponentTypesIconWrapper
                    dataKey={
                        (componentsTypeData as ComponentTypeMapped)?.data_key
                    }
                />
            ),
            [componentsTypeData],
        );

        const showContentTag = useMemo<boolean>(
            () =>
                !!formula?.id &&
                !!formulaType &&
                !!FormulaTypesData[formulaType].supportContentTag,
            [formula, formulaType],
        );

        const toggleButtons = useMemo(() => {
            const baseButtons: ToggleButtonProps[] = [
                {
                    title: isDataset
                        ? 'View Dataset'
                        : isCMSPlaceholder
                        ? 'Abandoned cart'
                        : componentsTypeData?.name || 'View alternates',
                    color: componentsTypeData?.base_color,
                    value: ToggleButtonValues.VIEW_VARIATION,
                    isLeft: true,
                    isSelected: isVariationTab,
                    onChange: setIsVariationTab,
                    icon: ComponentIcon && ComponentIcon,
                    tooltip:
                        !isDataset &&
                        !isCMSPlaceholder &&
                        'Add and manage text alternates and add personalization labels.',
                },
                {
                    title: 'Preview',
                    value: ToggleButtonValues.PREVIEW,
                    isSelected: isVariationTab,
                    onChange: setIsVariationTab,
                    isRight: !showContentTag,
                    tooltip:
                        'See a preview of how these messages will look like on a phone.',
                },
            ];

            if (showContentTag) {
                baseButtons.push({
                    title: 'Content Tag',
                    value: ToggleButtonValues.CONTENT_TAG,
                    isSelected: isVariationTab,
                    isRight: true,
                    onChange: setIsVariationTab,
                    tooltip: 'Copy your content URL',
                });
            }
            return baseButtons;
        }, [
            isDataset,
            isCMSPlaceholder,
            componentsTypeData,
            isVariationTab,
            setIsVariationTab,
            ComponentIcon,
            showContentTag,
        ]);

        return (
            <Container>
                {!hideToggleButtons && (
                    <AampeToggleButton buttons={toggleButtons} />
                )}
                <Steps>
                    {isVariationTab === ToggleButtonValues.VIEW_VARIATION && (
                        <FormulaSidebar
                            selectedComponent={selectedElement}
                            data={
                                selectedElement
                                    ? components.find(
                                          (
                                              component: FormulaComponentModelV1,
                                          ) =>
                                              component.id ===
                                              selectedElement.elementId,
                                      )
                                    : null
                            }
                            removeSpacing={hideToggleButtons}
                        />
                    )}
                    {isVariationTab === ToggleButtonValues.PREVIEW &&
                        !hideToggleButtons && <MessagePreviewMain />}
                    {isVariationTab === ToggleButtonValues.CONTENT_TAG && (
                        <ContentTag formulaId={formula!.id} />
                    )}
                </Steps>
            </Container>
        );
    };

const Container = styled.div(() => ({
    width: '48%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
    height: '100%',
}));

const Steps = styled.div(() => ({
    height: '95%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
}));

export default SidebarViewMain;
